import { Component, createContext, useContext, useLayoutEffect, useRef } from 'react';
import { createPortal, render } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { usePrefersCreateRoot } from 'react-create-root-preference';
import { jsx as _jsx } from "react/jsx-runtime";
export const PortalLayerContext = /*#__PURE__*/createContext({
  preserveScrollOnClose: false
});
function saveScrollPositions(el) {
  return {
    top: el.scrollTop,
    left: el.scrollLeft,
    children: Array.from(el.children).map(saveScrollPositions)
  };
}
function restoreScrollPositions(el, snapshot) {
  el.scrollTop = snapshot.top;
  el.scrollLeft = snapshot.left;
  Array.from(el.children).forEach((child, i) => {
    if (snapshot.children[i]) {
      restoreScrollPositions(child, snapshot.children[i]);
    }
  });
}
function appendTransitionContents(closeTransitionContainer, originalTransitionContents) {
  originalTransitionContents.forEach(({
    clone,
    scrollSnapshot
  }) => {
    if (closeTransitionContainer.firstChild !== null) {
      closeTransitionContainer.firstChild.appendChild(clone);
      if (scrollSnapshot) {
        restoreScrollPositions(clone, scrollSnapshot);
      }
    }
  });
}

// See https://github.com/reactwg/react-18/discussions/5#discussioncomment-2276079
function RenderCallback({
  children,
  callback
}) {
  const once = useRef(false);
  useLayoutEffect(() => {
    if (once.current) return;
    once.current = true;
    callback();
  }, [callback]);
  return children;
}
class PortalLayer extends Component {
  componentWillUnmount() {
    const {
      element,
      preserveScrollOnClose,
      renderTree,
      prefersCreateRoot,
      setCloseTransitionRootCleanup
    } = this.props;
    const closeTransitionTree = renderTree(null);
    if (!closeTransitionTree) return;
    const firstChild = element.firstChild;
    if (!firstChild || !firstChild.children) return;
    const originalTransitionContents = Array.from(firstChild.children).map(childElement => ({
      clone: childElement.cloneNode(true),
      scrollSnapshot: preserveScrollOnClose ? saveScrollPositions(childElement) : null
    }));
    const closeTransitionContainer = document.createElement('div');
    element.appendChild(closeTransitionContainer);
    closeTransitionContainer.addEventListener('click', e => {
      // clones are not interactive, preventing unexpected behavior during close
      e.preventDefault();
    });
    if (prefersCreateRoot) {
      const closeTransitionRoot = createRoot(closeTransitionContainer);
      setCloseTransitionRootCleanup(() => {
        closeTransitionRoot.unmount();
        setCloseTransitionRootCleanup(null);
      });
      closeTransitionRoot.render( /*#__PURE__*/_jsx(RenderCallback, {
        callback: () => {
          appendTransitionContents(closeTransitionContainer, originalTransitionContents);
        },
        children: closeTransitionTree
      }));
    } else {
      render(closeTransitionTree, closeTransitionContainer, () => {
        appendTransitionContents(closeTransitionContainer, originalTransitionContents);
      });
    }
  }
  render() {
    const {
      children,
      element,
      renderTree
    } = this.props;
    const tree = renderTree(children);
    return /*#__PURE__*/createPortal(tree, element);
  }
}
PortalLayer.displayName = 'PortalLayer';
function PortalLayerWithContext(props) {
  const {
    preserveScrollOnClose
  } = useContext(PortalLayerContext);
  const prefersCreateRoot = usePrefersCreateRoot();
  return /*#__PURE__*/_jsx(PortalLayer, Object.assign({}, props, {
    preserveScrollOnClose: preserveScrollOnClose,
    prefersCreateRoot: prefersCreateRoot
  }));
}
export { PortalLayerWithContext as PortalLayer };